import React, { useEffect, useState } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Mail, Send, RefreshCw, Trash2, UserPlus, Loader2 } from 'lucide-react'
import { useToast } from '@/contexts/ToastContext'
import { invitesApi, type UserInvite, type InviteStatus } from '@/lib/invitesApi'
import { useTranslation } from 'react-i18next'

function fmtDate(iso: string | null): string {
  if (!iso) return '—'
  try { return new Date(iso).toLocaleString() } catch { return iso }
}

function StatusBadge({ status }: { status: InviteStatus }) {
  const { t } = useTranslation()
  const tone: Record<InviteStatus, string> = {
    pending:  'bg-amber-100 text-amber-700 border-amber-200',
    accepted: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    revoked:  'bg-slate-100 text-slate-600 border-slate-200',
    expired:  'bg-rose-100 text-rose-700 border-rose-200',
  }
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium border ${tone[status]}`}>
      {t(`invites.status.${status}`, { defaultValue: status })}
    </span>
  )
}

export const InvitesSection: React.FC = () => {
  const { t } = useTranslation()
  const { success, error: showError } = useToast()
  const [invites, setInvites] = useState<UserInvite[]>([])
  const [loading, setLoading] = useState(true)
  const [email, setEmail] = useState('')
  const [sending, setSending] = useState(false)
  const [busyId, setBusyId] = useState<string | null>(null)

  const load = async () => {
    setLoading(true)
    try {
      const rows = await invitesApi.list()
      setInvites(rows)
    } catch (err: any) {
      showError('Failed to load invites', err?.message)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { void load() }, [])

  const validEmail = (s: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s.trim())

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault()
    if (sending) return
    if (!validEmail(email)) {
      showError('Invalid email')
      return
    }
    setSending(true)
    try {
      const result = await invitesApi.create(email.trim().toLowerCase())
      if (result.email_sent) {
        success(`Invite sent to ${result.email}`)
      } else {
        showError(`Invite created for ${result.email} but email send failed. Check server logs.`)
      }
      setEmail('')
      await load()
    } catch (err: any) {
      showError('Failed to create invite', err?.message)
    } finally {
      setSending(false)
    }
  }

  const handleRevoke = async (invite: UserInvite) => {
    if (!confirm(`Revoke invite for ${invite.email}? The link will stop working immediately.`)) return
    setBusyId(invite.id)
    try {
      await invitesApi.revoke(invite.id)
      success('Invite revoked')
      await load()
    } catch (err: any) {
      showError('Failed to revoke', err?.message)
    } finally {
      setBusyId(null)
    }
  }

  const handleResend = async (invite: UserInvite) => {
    setBusyId(invite.id)
    try {
      const result = await invitesApi.resend(invite.id)
      if (result.email_sent) {
        success(`Invite resent to ${result.email}`)
      } else {
        showError(`New invite created for ${result.email} but email send failed.`)
      }
      await load()
    } catch (err: any) {
      showError('Failed to resend', err?.message)
    } finally {
      setBusyId(null)
    }
  }

  return (
    <Card className="border-2 border-emerald-200 dark:border-emerald-800">
      <CardHeader className="bg-gradient-to-r from-emerald-50 to-teal-50 dark:from-emerald-950 dark:to-teal-950">
        <CardTitle className="flex items-center gap-2">
          <Mail className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
          <span className="text-emerald-900 dark:text-emerald-100">{t('invites.title')}</span>
        </CardTitle>
        <CardDescription className="text-emerald-700 dark:text-emerald-300">
          Invite new users by email. Registration is invite-only.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6 pt-6">

        {/* Create invite form */}
        <form onSubmit={handleCreate} className="flex flex-col sm:flex-row gap-2">
          <Input
            type="email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            placeholder="new.user@example.com"
            disabled={sending}
            className="flex-1"
          />
          <Button type="submit" disabled={sending || !email.trim()}>
            {sending ? (
              <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> {t('invites.sending')}</>
            ) : (
              <><UserPlus className="h-4 w-4 mr-1" /> {t('invites.send')}</>
            )}
          </Button>
        </form>

        {/* Invites table */}
        <div className="rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden">
          <div className="px-3 py-2 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/40 flex items-center justify-between">
            <span className="text-[10px] uppercase tracking-wide text-slate-500 dark:text-slate-400">
              Recent invites
            </span>
            <button
              type="button"
              onClick={load}
              disabled={loading}
              className="text-xs text-slate-500 hover:text-slate-800 dark:hover:text-slate-200 inline-flex items-center gap-1 disabled:opacity-50"
            >
              <RefreshCw className={`h-3 w-3 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </button>
          </div>

          {loading ? (
            <div className="p-6 text-center text-xs text-slate-400">{t('common.loading')}</div>
          ) : invites.length === 0 ? (
            <div className="p-6 text-center text-xs text-slate-500">{t('invites.empty')}</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 dark:bg-slate-900/30">
                  <tr className="text-left text-slate-500 dark:text-slate-400">
                    <th className="px-3 py-2 font-medium">{t('fields.email')}</th>
                    <th className="px-3 py-2 font-medium">{t('fields.status')}</th>
                    <th className="px-3 py-2 font-medium">Expires</th>
                    <th className="px-3 py-2 font-medium">Invited by</th>
                    <th className="px-3 py-2 font-medium">{t('fields.createdAt')}</th>
                    <th className="px-3 py-2 font-medium text-right">{t('common.actions')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-700/60">
                  {invites.map(i => (
                    <tr key={i.id} className="hover:bg-slate-50 dark:hover:bg-slate-700/40">
                      <td className="px-3 py-2 font-mono">{i.email}</td>
                      <td className="px-3 py-2"><StatusBadge status={i.status} /></td>
                      <td className="px-3 py-2 text-slate-500" title={i.expires_at}>{fmtDate(i.expires_at)}</td>
                      <td className="px-3 py-2 text-slate-500">{i.invited_by_email ?? '—'}</td>
                      <td className="px-3 py-2 text-slate-500" title={i.created_at}>{fmtDate(i.created_at)}</td>
                      <td className="px-3 py-2 text-right">
                        {i.status === 'pending' ? (
                          <div className="inline-flex items-center gap-1">
                            <button
                              type="button"
                              onClick={() => handleResend(i)}
                              disabled={busyId === i.id}
                              className="text-blue-600 hover:text-blue-800 dark:text-blue-400 inline-flex items-center gap-1 text-[11px] disabled:opacity-50"
                              title={t('invites.resend')}
                            >
                              <Send className="h-3 w-3" /> Resend
                            </button>
                            <span className="text-slate-300">|</span>
                            <button
                              type="button"
                              onClick={() => handleRevoke(i)}
                              disabled={busyId === i.id}
                              className="text-rose-600 hover:text-rose-800 inline-flex items-center gap-1 text-[11px] disabled:opacity-50"
                              title={t('invites.revoke')}
                            >
                              <Trash2 className="h-3 w-3" /> Revoke
                            </button>
                          </div>
                        ) : (
                          <span className="text-slate-300">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  )
}
